import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Activity, AlertTriangle, CheckCircle2, RotateCcw, Zap, Loader2,
  FileWarning, Trash2, Clock, Info, Eraser,
} from "lucide-react";

/**
 * AdminPdfMonitoring · dashboard for Bilan PDF generation health.
 * Route: /app/admin/pdf-monitoring
 *
 * Shows: 30-day success/failure counts, failure %, latency p50/p95,
 * top offending dossiers, last 10 failures (each expandable to full trace).
 * Also exposes the cache-cleanup one-shot maintenance command.
 */
export default function AdminPdfMonitoring() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [openTrace, setOpenTrace] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [purgeDays, setPurgeDays] = useState(7);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/reports/admin/pdf-monitoring?days=${days}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chargement impossible.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [days]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cleanup = async () => {
    if (!window.confirm("Coercer les valeurs numériques null en 0 pour tous les caches carrière + opportunités ?")) return;
    setCleaning(true);
    try {
      const { data: r } = await api.post("/reports/admin/cleanup-null-cache");
      toast.success(`Nettoyage : ${r.career_discoveries_touched} carrières · ${r.global_opportunity_matches_touched} opportunités.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nettoyage impossible.");
    } finally { setCleaning(false); }
  };

  const purge = async () => {
    const label = purgeDays === 0 ? "TOUS les échecs" : `les échecs de plus de ${purgeDays} j`;
    if (!window.confirm(
      `Supprimer ${label} ?\n\nLes événements de succès (KPI, latences) sont préservés. Action irréversible.`
    )) return;
    setPurging(true);
    try {
      const { data: r } = await api.post(
        `/reports/admin/pdf-monitoring/purge?older_than_days=${purgeDays}`
      );
      toast.success(
        r.deleted === 0
          ? "Rien à purger — la fenêtre est déjà propre."
          : `${r.deleted} échec${r.deleted > 1 ? "s" : ""} supprimé${r.deleted > 1 ? "s" : ""} du journal.`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge impossible.");
    } finally { setPurging(false); }
  };

  const failureRate = data?.failure_rate_pct ?? 0;
  const health = failureRate === 0 ? "green" : failureRate < 5 ? "amber" : "red";

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]" data-testid="admin-pdf-monitoring-root">
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-24">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
          Back-office · Monitoring Bilans PDF
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight leading-tight">
          Santé de la génération des Bilans<br />
          <span className="text-[#0B2545]">détecte les régressions avant vos clients.</span>
        </h1>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <label className="text-[11px] font-mono uppercase tracking-widest text-[#5B5B62]">Fenêtre</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                   data-testid="admin-pdf-mon-window"
                   className="rounded-md border border-[#E4E1D8] px-3 py-1.5 text-xs">
            <option value={1}>24 h</option>
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
          <button onClick={load} data-testid="admin-pdf-mon-refresh"
                   className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] hover:border-[#0B2545] px-3 py-1.5 text-xs text-[#5B5B62] hover:text-[#0B2545]">
            <RotateCcw className="h-3 w-3" strokeWidth={2} /> Rafraîchir
          </button>
          <button onClick={cleanup} disabled={cleaning}
                   data-testid="admin-pdf-mon-cleanup"
                   className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 px-3 py-1.5 text-xs">
            {cleaning ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> : <Trash2 className="h-3 w-3" strokeWidth={2} />}
            Nettoyer les caches null
          </button>
          <div className="inline-flex items-center gap-1 rounded-md border border-[#E4E1D8] pl-2 pr-1 py-0.5">
            <label className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
              Purger &gt;
            </label>
            <select value={purgeDays} onChange={(e) => setPurgeDays(Number(e.target.value))}
                     data-testid="admin-pdf-mon-purge-window"
                     className="rounded bg-transparent px-1 py-1 text-xs">
              <option value={0}>tous</option>
              <option value={1}>1 j</option>
              <option value={7}>7 j</option>
              <option value={30}>30 j</option>
              <option value={90}>90 j</option>
            </select>
            <button onClick={purge} disabled={purging}
                     data-testid="admin-pdf-mon-purge"
                     className="inline-flex items-center gap-1.5 rounded-md border border-red-300 text-red-800 hover:bg-red-50 px-2 py-1 text-xs">
              {purging ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> : <Eraser className="h-3 w-3" strokeWidth={2} />}
              Purger échecs
            </button>
          </div>
        </div>

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-[#5B5B62]">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> Chargement…
          </div>
        )}

        {data && (
          <>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-pdf-mon-kpis">
              <Kpi icon={Activity} label="Tentatives" value={data.total_attempts} accent="#0B2545" />
              <Kpi icon={CheckCircle2} label="Succès" value={data.successes} accent="#059669"
                    hint={data.retried_successes > 0 ? `dont ${data.retried_successes} au 2e essai` : null} />
              <Kpi icon={FileWarning} label="Échecs" value={data.failures}
                    accent={data.failures ? "#DC2626" : "#8B8B8F"} />
              <Kpi icon={Zap} label="Taux d'échec"
                    value={`${data.failure_rate_pct.toFixed(1)} %`}
                    accent={{green: "#059669", amber: "#B45309", red: "#DC2626"}[health]}
                    hint={{green: "Nominal", amber: "Vigilance", red: "Alerte"}[health]} />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <LatencyTile label="Latence moyenne" value={data.latency_ms?.avg} />
              <LatencyTile label="Latence P50" value={data.latency_ms?.p50} />
              <LatencyTile label="Latence P95" value={data.latency_ms?.p95} />
            </div>

            {data.top_failing_dossiers?.length > 0 && (
              <div className="mt-10 rounded-xl border border-[#E4E1D8] bg-white p-5"
                   data-testid="admin-pdf-mon-top">
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
                  Dossiers avec le plus d'échecs
                </div>
                <ul className="mt-3 space-y-2">
                  {data.top_failing_dossiers.map((t, i) => (
                    <li key={t.dossier_id} className="flex items-center gap-3 text-sm">
                      <span className="w-6 font-mono text-[10px] text-[#8B8B8F]">#{i + 1}</span>
                      <a href={`/app/dossiers/${t.dossier_id}`}
                         className="font-mono text-[#0B2545] hover:underline">
                        {t.dossier_id}
                      </a>
                      <span className="ml-auto text-xs text-[#5B5B62]">
                        {t.failure_count} échec{t.failure_count > 1 ? "s" : ""} · {t.last_error}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 rounded-xl border border-[#E4E1D8] bg-white overflow-hidden"
                 data-testid="admin-pdf-mon-failures">
              <div className="px-5 py-3 border-b border-[#E4E1D8] flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={2} />
                <div className="font-display text-lg text-[#0B2545] tracking-tight">
                  Derniers échecs
                </div>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                  {data.recent_failures?.length ?? 0}
                </span>
              </div>
              {(data.recent_failures || []).length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-[#5B5B62]">
                  Aucun échec récent. Bravo.
                </div>
              )}
              <ul className="divide-y divide-[#E4E1D8]">
                {(data.recent_failures || []).map((f) => (
                  <li key={f.event_id} className="px-5 py-3"
                      data-testid={`admin-pdf-mon-failure-${f.event_id}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] text-[#8B8B8F]">
                        {new Date(f.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      <span className="font-mono text-xs text-[#0B2545]">{f.dossier_id}</span>
                      <span className="text-xs font-medium text-red-700">{f.error_type}</span>
                      <span className="text-xs text-[#5B5B62] truncate flex-1">{f.error_message}</span>
                      <button onClick={() => setOpenTrace(openTrace === f.event_id ? null : f.event_id)}
                              data-testid={`admin-pdf-mon-trace-${f.event_id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#7A5B14]">
                        <Info className="h-3 w-3" strokeWidth={2} />
                        {openTrace === f.event_id ? "Masquer" : "Trace"}
                      </button>
                    </div>
                    {openTrace === f.event_id && (
                      <TraceViewer eventId={f.event_id} />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function Kpi({ icon: Icon, label, value, accent, hint }) {
  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} strokeWidth={1.8} />
      </div>
      <div className="mt-2 font-mono text-2xl md:text-3xl text-[#0B2545] tabular-nums leading-none">
        {value}
      </div>
      {hint && <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">{hint}</div>}
    </div>
  );
}


function LatencyTile({ label, value }) {
  return (
    <div className="rounded-lg border border-[#E4E1D8] bg-white/70 px-4 py-3"
         data-testid={`admin-pdf-mon-latency-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">
        <Clock className="h-3 w-3" strokeWidth={2} /> {label}
      </div>
      <div className="mt-1 font-mono text-lg text-[#0B2545] tabular-nums">
        {value != null ? `${value} ms` : "—"}
      </div>
    </div>
  );
}


function TraceViewer({ eventId }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/admin/pdf-monitoring/failure/${eventId}`);
        setDetail(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Indisponible.");
      }
    })();
  }, [eventId]);

  if (error) return <div className="mt-3 text-xs text-amber-700">{error}</div>;
  if (!detail) return <div className="mt-3 text-xs text-[#5B5B62]">Chargement…</div>;
  return (
    <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-[#F8F7F3] border border-[#E4E1D8] p-3 text-[10px] font-mono text-[#3A3A3F] whitespace-pre-wrap">
{detail.traceback || "(pas de trace)"}
    </pre>
  );
}
